import { useMemo } from 'react';
import { routesModel } from '@Router/router.consts';
import { RoutesMap } from '@Router/router.types.ts';
import { OnboardingFlowIdentifier } from '@Types/onboarding.types';
import { AuthorizedUserOnboardingType } from '@Store/slices/authOnboarding/authOnboarding.types';
import i18next from '../../in18/in18';

export interface MenuItem {
  name: string;
  routes: readonly RoutesMap[];
}

const personalDetailsRoutes = [
  RoutesMap.WELCOME,
  RoutesMap.PERSONAL_DETAILS,
  RoutesMap.PHONE,
  RoutesMap.HOME_ADDRESS,
  RoutesMap.IDENTITY,
] as const;

const businessDetailsRoutes = [
  RoutesMap.BUSINESS_TYPE_SELECTION,
  RoutesMap.REGISTERED_ADDRESS,
  RoutesMap.BUSINESS_ADDRESS,
  RoutesMap.KNOW_YOUR_BUSINESS,
  RoutesMap.INDUSTRY,
  RoutesMap.OWNER_CENTER,
  RoutesMap.ADD_OWNER,
  RoutesMap.EDD_EMBEDDED_WHAT_DO_YOU_SELL,
  RoutesMap.EDD_EMBEDDED_TO_WHO_YOU_SELL,
  RoutesMap.EDD_EMBEDDED_WHY_DID_YOU_CHOOSE,
  RoutesMap.EDD_EMBEDDED_BUSINESS_COUNTRIES,
  RoutesMap.EDD_EMBEDDED_MONTHLY_PAYMENT_VOLUME,
  RoutesMap.EDD_WEBSITE_SOCIAL,
  RoutesMap.EDD_BUSINESS_CAPITAL,
  RoutesMap.EDD_FUNDING_SOURCES,
  RoutesMap.EDD_NONPROFIT_FUNDING_SOURCES,
  RoutesMap.EDD_NONPROFIT_DONORS,
  RoutesMap.EDD_NONPROFIT_ORGANIZATION_SIZE,
  RoutesMap.EDD_NONPROFIT_MONTHLY_ACTIVITY,
  RoutesMap.EDD_NONPROFIT_AFFILIATION,
  RoutesMap.EDD_NONPROFIT_SOCIAL_MEDIA,
  RoutesMap.EDD_ORGANIZATION_ROLE,
  RoutesMap.EDD_NONPROFIT_DESCRIPTION,
  RoutesMap.EDD_NONPROFIT_DESCRIPTION_B,
  RoutesMap.EDD_NONPROFIT_DESCRIPTION_C,
  RoutesMap.EDD_NONPROFIT_DESCRIPTION_D,
  RoutesMap.EDD_NONPROFIT_DESCRIPTION_E,
  RoutesMap.EDD_BENEFICIARY_SELECTION,
  RoutesMap.EDD_ADDITIONAL_BUSINESS_LOCATIONS,
  RoutesMap.EDD_INTERNATIONAL_ACTIVITY,
  RoutesMap.EDD_BUSINESS_SIZE,
  RoutesMap.EDD_ANNUAL_REVENUE,
  RoutesMap.EDD_INTENDED_USE,
  RoutesMap.EDD_SANCTIONED_COUNTRIES,
  RoutesMap.EDD_CHARITIES,
  RoutesMap.EDD_MILITARY_PRECIOUS_METALS,
  RoutesMap.EDD_CASH_PAYMENTS,
  RoutesMap.EDD_TAX_LEGAL_SERVICES,
  RoutesMap.EDD_FUND_TRANSFER,
  RoutesMap.EDD_INVESTMENT_CUSTODIAL,
  RoutesMap.MAILING_ADDRESS,
  RoutesMap.CDD_ACH_VOLUMES,
  RoutesMap.CDD_DOMESTIC_WIRE_VOLUMES,
  RoutesMap.CDD_INTERNATIONAL_WIRES_VOLUMES,
] as const;

const planRoutes = [RoutesMap.PLAN_SELECTION] as const;
const confirmYourDetailsRoutes = [RoutesMap.REVIEW] as const;

export const menuMap = [
  {
    name: i18next.t('passwordlessSideMenu.personalDetails'),
    routes: personalDetailsRoutes,
  },
  {
    name: i18next.t('passwordlessSideMenu.businessDetails'),
    routes: businessDetailsRoutes,
  },
  {
    name: i18next.t('passwordlessSideMenu.chooseAPlan'),
    routes: planRoutes,
  },
  {
    name: i18next.t('passwordlessSideMenu.confirmation'),
    routes: confirmYourDetailsRoutes,
  },
] as const satisfies MenuItem[];

export const uboSideMenuItems = [
  {
    name: i18next.t('pages.uboOnboarding.intro'),
    routes: [RoutesMap.UBO_ONBOARDING_INTRO] as const,
  },
  {
    name: i18next.t('pages.uboOnboarding.verification'),
    routes: [RoutesMap.UBO_ONBOARDING_VALIDATE_PHONE] as const,
  },
  {
    name: i18next.t('pages.uboOnboarding.securityQuestion'),
    routes: [RoutesMap.UBO_ONBOARDING_ID_VERIFICATION] as const,
  },
  {
    name: i18next.t('pages.uboOnboarding.createPassword'),
    routes: [RoutesMap.UBO_ONBOARDING_CREATE_PASSWORD] as const,
  },
] as const satisfies MenuItem[];

const authUserKYCMenuItems = [
  {
    name: i18next.t('inviteTeamMember:sideMenu.phoneNumber'),
    routes: [RoutesMap.AUTH_USER_PHONE, RoutesMap.VERIFY_PHONE_NUMBER] as const,
  },
  {
    name: i18next.t('inviteTeamMember:sideMenu.createPassword'),
    routes: [RoutesMap.SET_PASSWORD] as const,
  },
  {
    name: i18next.t('inviteTeamMember:sideMenu.personalDetails'),
    routes: [RoutesMap.AUTH_USER_IDENTITY, RoutesMap.AUTH_USER_HOME_ADDRESS] as const,
  },
  {
    name: i18next.t('inviteTeamMember:sideMenu.verification'),
    routes: [RoutesMap.AUTH_USER_DOCV] as const,
  },
] as const satisfies MenuItem[];

const authUserNoKYCMenuItems = [
  {
    name: i18next.t('inviteTeamMember:accountantPortalSideMenu.phoneNumber'),
    routes: [RoutesMap.AUTH_USER_PHONE, RoutesMap.VERIFY_PHONE_NUMBER] as const,
  },
  {
    name: i18next.t('inviteTeamMember:accountantPortalSideMenu.createPassword'),
    routes: [RoutesMap.SET_PASSWORD] as const,
  },
] as const satisfies MenuItem[];

const authUserKycOnlyMenuItems = [
  {
    name: i18next.t('inviteTeamMember:sideMenu.phoneNumber'),
    routes: [RoutesMap.VERIFY_PHONE_NUMBER] as const,
  },
  {
    name: i18next.t('inviteTeamMember:sideMenu.personalDetails'),
    routes: [RoutesMap.AUTH_USER_IDENTITY, RoutesMap.AUTH_USER_HOME_ADDRESS] as const,
  },
] as const satisfies MenuItem[];

export const getSideMenuItemsFromOnboardingFlowIdentifier = (
  onboardingFlowIdentifier: OnboardingFlowIdentifier,
  onboardingType: AuthorizedUserOnboardingType | undefined
) => {
  switch (onboardingFlowIdentifier) {
    case OnboardingFlowIdentifier.UBO:
      return uboSideMenuItems;
    case OnboardingFlowIdentifier.AUTHORIZED_USER:
    case OnboardingFlowIdentifier.AUTHORIZED_USER_ACCOUNTANT_PORTAL:
      if (onboardingType === AuthorizedUserOnboardingType.AUTHORIZEDUSER_NOKYC) {
        return authUserNoKYCMenuItems;
      }
      if (onboardingType === AuthorizedUserOnboardingType.KYC_ONLY) {
        return authUserKycOnlyMenuItems;
      }
      return authUserKYCMenuItems;
    default:
      return menuMap;
  }
};

export const PAGES_TO_HIDE: RoutesMap[] = [
  RoutesMap.WELCOME,
  RoutesMap.INVITE_TEAM_MEMBER_INTRO,
  RoutesMap.INVITE_TEAM_MEMBER_FINISHED,
  RoutesMap.SUCCESS,
  RoutesMap.SUCCESS_WITHOUT_AUTO_LOGIN,
  RoutesMap.KYB_PENDING,
  RoutesMap.UNDER_REVIEW,
  RoutesMap.VERIFICATION_FAILED,
  RoutesMap.ACCOUNTANT_PORTAL_WELCOME,
  RoutesMap.ACCOUNTANT_PORTAL_COMPLETE,
  RoutesMap.DNB_EMBEDDED_INTRO,
];

export interface SidebarLogicResult {
  shouldShowItems: boolean;
  activeRouteIndex: number;
}

export const useSidebarLogic = (pathname: string, menuItems: MenuItem[]): SidebarLogicResult => {
  const shouldShowItems = useMemo(
    () =>
      menuItems.some(({ routes }) => routes.some((route) => pathname === routesModel[route].path)),
    [pathname, menuItems]
  );

  const activeRouteIndex = useMemo(() => {
    let index = -1;
    menuItems.some((item, idx) => {
      if (item.routes.some((route) => pathname === routesModel[route].path)) {
        index = idx;
        return true;
      }
      return false;
    });
    return index;
  }, [pathname, menuItems]);

  return {
    shouldShowItems,
    activeRouteIndex,
  };
};
